import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Table,
  Input,
  DatePicker,
  Pagination,
  Modal,
  message,
} from "antd"
import {
  SearchOutlined,
  DownOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import { jwtDecode } from "jwt-decode"
import { translations } from "../../utils/Translation/staticTranslation"
import { SalesRequests as SalesRequestsApi } from "../../Apis/salesRequestsApi"
import SchedulingApi from "../../Apis/schedulingApi"
import MeetingsTab from "../../components/Scheduling/MeetingsTab"
import AvailabilityTab from "../../components/Scheduling/AvailabilityTab"
import {
  isAccountSuperuser,
  isProfileModuleDeleteBlocked,
} from "../../utils/modulePermissionSidebar"
import { getUserCookies } from "../../utils/Cookies"
import "../../components/Contacts/Contacts.css"
import "../../components/Scheduling/Scheduling.css"
import "./SalesRequests.css"

const { RangePicker } = DatePicker

const PAGE_SIZE = 10

const RequestsTab = () => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.salesRequestsPage || {}
  const isRTL = lang === "ar"
  const isFetchingRef = useRef(false)
  const user = useSelector((state) => state.user.user)
  const canDelete = !isProfileModuleDeleteBlocked(user, "sales-requests")

  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [dateRange, setDateRange] = useState({ from: null, to: null })
  const [detail, setDetail] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [movingId, setMovingId] = useState(null)

  // Debounced so typing a company name does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 600)
    return () => clearTimeout(timer)
  }, [search])

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, dateRange.from, dateRange.to])

  const fetchSalesRequests = useCallback(async () => {
    if (isFetchingRef.current) return
    isFetchingRef.current = true

    try {
      setLoading(true)
      const response = await SalesRequestsApi.getSalesRequests({
        search: debouncedSearch || "",
        dateFrom: dateRange.from || "",
        dateTo: dateRange.to || "",
        page,
        pageSize: PAGE_SIZE,
      })

      const data = response?.data || {}
      setRows(Array.isArray(data.results) ? data.results : [])
      setTotal(data.count || 0)
    } catch (error) {
      console.error("Error fetching sales requests:", error)
      const currentT = translations[lang]?.salesRequestsPage || {}
      message.error(currentT?.error_fetching || "Error fetching sales requests")
    } finally {
      setLoading(false)
      isFetchingRef.current = false
    }
  }, [debouncedSearch, dateRange.from, dateRange.to, page, lang])

  useEffect(() => {
    fetchSalesRequests()
  }, [fetchSalesRequests])

  const handleDelete = (record) => {
    if (!canDelete) return
    Modal.confirm({
      title: t?.delete_confirm_title || "Delete this request?",
      icon: <ExclamationCircleFilled />,
      content: t?.delete_confirm_body || "This action cannot be undone.",
      okText: t?.delete_confirm_ok || "Delete",
      okButtonProps: { danger: true },
      cancelText: t?.delete_confirm_cancel || "Cancel",
      centered: true,
      onOk: async () => {
        try {
          setDeletingId(record.id)
          await SalesRequestsApi.deleteSalesRequest(record.id)
          message.success(t?.delete_success || "Request deleted")
          // Close the detail modal if it was showing the deleted row.
          setDetail((prev) => (prev?.id === record.id ? null : prev))
          // Stepping back a page keeps the table from landing on an empty page
          // after deleting the last row of the last page.
          if (rows.length === 1 && page > 1) {
            setPage((prev) => prev - 1)
          } else {
            fetchSalesRequests()
          }
        } catch (error) {
          console.error("Error deleting sales request:", error)
          message.error(t?.delete_failed || "Error deleting sales request")
          // Not rethrown, for the same reason as the status change above.
        } finally {
          setDeletingId(null)
        }
      },
    })
  }

  const handleMoveToMerchant = (record) => {
    Modal.confirm({
      title: t?.move_confirm_title || "Move to merchant requests?",
      icon: <ExclamationCircleFilled />,
      content:
        t?.move_confirm_body ||
        "This request moves to the Merchants pending list, where you can accept or reject it.",
      okText: t?.move_confirm_ok || "Move",
      cancelText: t?.move_confirm_cancel || "Cancel",
      centered: true,
      onOk: async () => {
        try {
          setMovingId(record.id)
          await SalesRequestsApi.moveToMerchant(record.id)
          message.success(t?.move_success || "Moved to merchant requests")
          setDetail((prev) => (prev?.id === record.id ? null : prev))
          // The row has left this table, so mirror the delete flow and step
          // back when it was the last row of a trailing page.
          if (rows.length === 1 && page > 1) {
            setPage((prev) => prev - 1)
          } else {
            fetchSalesRequests()
          }
        } catch (error) {
          console.error("Error moving sales request:", error)
          const data = error?.response?.data
          const fieldError = Array.isArray(data?.status)
            ? data.status[0]
            : data?.status
          const serverError =
            fieldError ||
            data?.message ||
            data?.detail ||
            // exception_handler() wraps unexpected server errors as
            // { success: false, details: "..." }.
            data?.details
          // The backend spells the duplicate-email case out in its own words;
          // show our shorter, translated wording instead.
          const isDuplicateEmail =
            typeof serverError === "string" &&
            /email/i.test(serverError) &&
            /already|exists|duplicate/i.test(serverError)

          message.error(
            (isDuplicateEmail
              ? t?.move_duplicate_email ||
                "Already have a request with same email, please use different one."
              : serverError) ||
              t?.move_failed ||
              "Error moving sales request",
            8
          )
          // Not rethrown: a rejected onOk surfaces as an unhandled rejection
          // and CRA's dev overlay covers the message above.
        } finally {
          setMovingId(null)
        }
      },
    })
  }

  // Exports every row matching the current filters, not just the visible page.
  const handleExport = async () => {
    if (exporting) return

    try {
      setExporting(true)
      const response = await SalesRequestsApi.exportSalesRequests({
        search: debouncedSearch || "",
        dateFrom: dateRange.from || "",
        dateTo: dateRange.to || "",
      })

      const blob = new Blob([response?.data], { type: "text/csv;charset=utf-8" })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = downloadUrl
      link.setAttribute(
        "download",
        `sales_requests_${dayjs().format("YYYY-MM-DD")}.csv`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)

      message.success(t?.export_success || "Export downloaded")
    } catch (error) {
      console.error("Error exporting sales requests:", error)
      message.error(t?.export_error || "Error exporting sales requests")
    } finally {
      setExporting(false)
    }
  }

  const emptyCell = <span className="sales-request-empty">—</span>

  const columns = [
    {
      title: t?.table?.name || "Name",
      dataIndex: "name",
      key: "name",
      render: (text) =>
        text ? (
          <div style={{ fontWeight: 500, color: "#101828" }}>{text}</div>
        ) : (
          emptyCell
        ),
    },
    {
      title: t?.table?.email || "Email",
      dataIndex: "email",
      key: "email",
      render: (text) =>
        text ? (
          <a href={`mailto:${text}`} className="sales-request-link">
            {text}
          </a>
        ) : (
          emptyCell
        ),
    },
    {
      title: t?.table?.phone_number || "Phone",
      dataIndex: "phone_number",
      key: "phone_number",
      render: (text) =>
        text ? (
          <a href={`tel:${text}`} className="sales-request-link" dir="ltr">
            {text}
          </a>
        ) : (
          emptyCell
        ),
    },
    {
      title: t?.table?.actions || "Actions",
      key: "actions",
      align: "center",
      width: 120,
      render: (_, record) => (
        <div className="sales-request-actions-cell">
          <EyeOutlined
            className="sales-request-eye-icon"
            onClick={() => setDetail(record)}
            aria-label={t?.view || "View"}
          />
          {canDelete && (
            <DeleteOutlined
              className="sales-request-delete-icon"
              onClick={() => deletingId === null && handleDelete(record)}
              style={{
                cursor: deletingId !== null ? "not-allowed" : "pointer",
                opacity: deletingId !== null ? 0.45 : 1,
              }}
              aria-label={t?.delete || "Delete"}
            />
          )}
        </div>
      ),
    },
  ]

  const formatDate = (value) =>
    value ? (
      <span dir="ltr">{dayjs(value).format("YYYY-MM-DD HH:mm")}</span>
    ) : (
      emptyCell
    )

  const detailRows = detail
    ? [
        { label: t?.detail?.name || "Name", value: detail.name },
        {
          label: t?.detail?.business_name || "Business name",
          value: detail.business_name,
        },
        { label: t?.detail?.email || "Email", value: detail.email },
        {
          label: t?.detail?.phone_number || "Phone number",
          value: detail.phone_number ? (
            <span dir="ltr">{detail.phone_number}</span>
          ) : null,
        },
        {
          label: t?.detail?.furniture_type || "Furniture type",
          value: detail.furniture_type,
        },
        {
          label: t?.detail?.business_type || "Business type",
          value: detail.business_type,
        },
        { label: t?.detail?.message || "Message", value: detail.message },
        {
          label: t?.detail?.submitted_at || "Submitted at",
          value: formatDate(detail.time_created),
        },
        {
          label: t?.detail?.updated_at || "Last updated",
          value: formatDate(detail.time_updated),
        },
      ]
    : []

  return (
    /* The page shell (title and tabs) lives in the wrapper below; this is the
       Requests tab's own content, unchanged. */
    <div className="sales-requests-content">
      <div className="contacts-table-container contacts-table-wrapper">
        <div className="contact-filters-container">
          <Input
            placeholder={
              t?.search_placeholder || "Search by name, business, email or phone"
            }
            prefix={<SearchOutlined className="contact-search-icon" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="contact-search-input"
            allowClear
          />

          <div className="contact-filters-right">
            <RangePicker
              className="sales-request-range"
              format="YYYY-MM-DD"
              placeholder={[t?.date_start || "From", t?.date_end || "To"]}
              value={
                dateRange.from && dateRange.to
                  ? [dayjs(dateRange.from), dayjs(dateRange.to)]
                  : null
              }
              onChange={(dates) => {
                if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
                  setDateRange({ from: null, to: null })
                  return
                }
                setDateRange({
                  from: dates[0].format("YYYY-MM-DD"),
                  to: dates[1].format("YYYY-MM-DD"),
                })
              }}
            />

            <button
              type="button"
              className="sales-request-export-btn"
              onClick={handleExport}
              disabled={exporting || total === 0}
            >
              {exporting ? t?.exporting || "Exporting..." : t?.export || "Export"}
              <DownOutlined />
            </button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          scroll={{ x: 600 }}
          locale={{ emptyText: t?.empty || "No sales requests yet" }}
          pagination={false}
        />
      </div>

      {total > 0 && (
        <div className="d-flex justify-content-end mt-24">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            showTotal={(totalCount, range) =>
              isRTL
                ? `⁦${range[0]}-${range[1]}⁩ من ⁦${totalCount}⁩`
                : `${range[0]}-${range[1]} of ${totalCount}`
            }
            onChange={(nextPage) => setPage(nextPage)}
          />
        </div>
      )}

      <Modal
        open={Boolean(detail)}
        onCancel={() => setDetail(null)}
        footer={null}
        width={560}
        centered
        title={t?.detail_title || "Sales request details"}
        className="sales-request-detail-modal"
        closeIcon={<span className="sales-request-modal-close">×</span>}
      >
        <div className="sales-request-detail-list">
          {detailRows.map((row) => (
            <div className="sales-request-detail-row" key={row.label}>
              <span className="sales-request-detail-label">{row.label}</span>
              <span className="sales-request-detail-value">
                {row.value || emptyCell}
              </span>
            </div>
          ))}
        </div>

        {detail && (
          <div className="sales-request-detail-actions">
            <button
              type="button"
              className="sales-request-detail-btn sales-request-detail-btn-primary"
              disabled={movingId !== null}
              onClick={() => handleMoveToMerchant(detail)}
            >
              {t?.move_to_merchant || "Move to merchant requests"}
            </button>
          </div>
        )}

      </Modal>
    </div>
  )
}

/**
 * Sales Requests with tabs.
 *
 * Requests is unchanged and stays the initial tab. Meetings and Availability
 * are added for verified super administrators only — `isAccountSuperuser`
 * reads the account flag, not `effectiveHighestRole`, which also reports
 * SUPERADMIN for delegated market-portal roles. The backend re-checks
 * `is_superuser` on every scheduling call regardless of what this renders.
 */
const SalesRequests = () => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.salesRequestsPage || {}
  const ts = translations[lang]?.schedulingTab || {}
  const isRTL = lang === "ar"

  const user = useSelector((state) => state.user.user)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const canSchedule = isAccountSuperuser(user, decoded)

  const [activeTab, setActiveTab] = useState("requests")
  const [settings, setSettings] = useState(null)

  const loadSettings = useCallback(async () => {
    if (!canSchedule) return
    try {
      const response = await SchedulingApi.getSettings()
      setSettings(response?.data || null)
    } catch (error) {
      // A failure here only hides scheduling detail; Requests is unaffected.
      console.error("Error fetching scheduling settings:", error)
    }
  }, [canSchedule])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const tabs = [
    { key: "requests", label: t?.title || "Sales Requests" },
    ...(canSchedule
      ? [
          { key: "meetings", label: ts?.meetings_tab || "Meetings" },
          { key: "availability", label: ts?.availability_tab || "Availability" },
        ]
      : []),
  ]

  return (
    <div className="sales-requests-page">
      <h1 className="sales-requests-page-title">{t?.title || "Sales Requests"}</h1>

      {tabs.length > 1 ? (
        <div className="sch-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`sch-tab-button${activeTab === tab.key ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      {activeTab === "requests" ? <RequestsTab /> : null}

      {canSchedule && activeTab === "meetings" ? (
        <MeetingsTab
          t={ts}
          lang={lang}
          isRTL={isRTL}
          settings={settings}
          onSettingsChanged={loadSettings}
        />
      ) : null}

      {canSchedule && activeTab === "availability" && settings ? (
        <AvailabilityTab
          t={ts}
          lang={lang}
          isRTL={isRTL}
          settings={settings}
          onSettingsChanged={(updated) => (updated ? setSettings(updated) : loadSettings())}
        />
      ) : null}
    </div>
  )
}

export default SalesRequests
